import React from 'react';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import Text from '../../text/Text';
import DatePickerHeader from './DatePickerHeader';
import PopulateWeeks from './PopulateWeek';
import PopulateYears from './PopulateYears';
import PopulateMonths from './PopulateMonth';
import PopulateHeaders from './PopulateHeaders';
import { SELECT_VIEW } from './constant';
import PopulateHour from './PopulateHour';
import PopulateMinute from './PopulateMinute';
import { isInvalidDate } from './helper';
import { DatePickerProps } from './types';

dayjs.extend(isBetween);

const DatePicker = ({
  date,
  startDate,
  endDate,
  onChange,
  displayDate,
  minDate,
  maxDate,
  range = false,
  dayHeaders = true,
  selecting,
  isTimePicker,
  isMonthPicker,
  isYearPicker,
  activeSelectView,
  setActiveSelectView,
  setDisplayedDate,
  setIsOpen,
}: DatePickerProps) => {
  const selected = (dates: Dayjs) => {
    return (
      (startDate &&
        endDate &&
        dates.isBetween(startDate, endDate, null, '[]')) ||
      (startDate && dates.isSame(startDate, 'day')) ||
      (date && dates.isSame(date, 'day'))
    );
  };

  const disabled = (dates: Dayjs) => {
    return (
      (minDate && dates.isBefore(minDate, 'day')) ||
      (maxDate && dates.isAfter(maxDate, 'day'))
    );
  };

  const generateDay = (i: number, selected: boolean, disabled: boolean) => (
    <div key={`day-${i}`} onClick={() => !disabled && onSelect(i)}>
      <div className="pointer date-picker-day">
        <div
          className={classNames(
            'd-flex justify-content-center align-items-center date-picker-day',
            {
              'bg-primary rounded-circle': selected,
            },
          )}
        >
          <Text
            size="sm"
            className={classNames('text-center', {
              'text-white': selected,
              'opacity-25': disabled,
            })}
          >
            {i}
          </Text>
        </div>
      </div>
    </div>
  );

  const onSelect = (day: number) => {
    const date = dayjs(displayDate).set('date', day);
    if (range) {
      if (selecting) {
        if (date.isBefore(startDate, 'day')) {
          onChange({
            startDate: date,
            endDate: startDate,
            selecting: false,
          });
        } else {
          onChange({
            startDate: startDate,
            endDate: date,
            selecting: !selecting,
          });
        }
      } else {
        onChange({
          date: null,
          endDate: null,
          startDate: date,
          selecting: !selecting,
        });
      }
    } else {
      if (isTimePicker) {
        setActiveSelectView(SELECT_VIEW.HOUR);
      }
      onChange({
        date,
        startDate: null,
        endDate: null,
      });
    }

    if (!isTimePicker && !range) {
      setIsOpen(false);
    }

    if (range && selecting) {
      setIsOpen(false);
    }
  };

  const showDate = () => {
    if (!range) {
      if (!isInvalidDate(date)) {
        return date?.format('MMM D, YYYY');
      }
    }
    if (range) {
      let date = '';
      if (!isInvalidDate(startDate)) {
        date = startDate?.format('MMM D, YYYY');
      }
      if (!isInvalidDate(endDate)) {
        date =
          startDate?.format('MMM D, YYYY') +
          '--' +
          endDate?.format('MMM D, YYYY');
      }

      return date;
    }
  };

  const onSelectYear = (year: number) => {
    const date = dayjs(displayDate).set('year', year);

    onChange({
      date,
      startDate: null,
      endDate: null,
    });
    setActiveSelectView(SELECT_VIEW.DATE);
  };

  const onSelectMonth = (month: number) => {
    const date = dayjs(displayDate).set('month', month);

    onChange({
      date,
      startDate: null,
      endDate: null,
    });
    setActiveSelectView(SELECT_VIEW.DATE);
  };

  const onSelectToday = () => {
    const date = dayjs(displayDate)
      .set('date', dayjs().date())
      .set('month', dayjs().month())
      .set('year', dayjs().year());
    onChange({
      date,
      startDate: null,
      endDate: null,
    });
  };

  const onSelectHour = (hour: number) => {
    const date = dayjs(displayDate).set('hour', hour);

    onChange({
      date,
      startDate: null,
      endDate: null,
    });
    setActiveSelectView(SELECT_VIEW.MINUTE);
  };

  const onSelectMinute = (minute: number) => {
    const date = dayjs(displayDate).set('minute', minute);

    onChange({
      date,
      startDate: null,
      endDate: null,
    });
    setActiveSelectView(SELECT_VIEW.DATE);
    setIsOpen(false);
  };

  return (
    <div className="d-flex justify-content-center">
      <div className="bg-white rounded-4 date-picker-wrapper">
        <DatePickerHeader
          range={range}
          displayDate={displayDate}
          showDate={showDate}
          setDisplayedDate={setDisplayedDate}
          setActiveSelectView={setActiveSelectView}
          onSelectToday={onSelectToday}
          minDate={minDate}
          maxDate={maxDate}
          isMonthPicker={isMonthPicker}
          isYearPicker={isYearPicker}
        />

        <div className="py-6 py-10 w-100">
          {activeSelectView === SELECT_VIEW.DATE &&
          !isMonthPicker &&
          !isYearPicker ? (
            <>
              {dayHeaders && (
                <div className="d-flex flex-wrap justify-content-evenly pb-3">
                  <PopulateHeaders displayDate={displayDate} />
                </div>
              )}
              <PopulateWeeks
                displayDate={displayDate}
                selected={selected}
                disabled={disabled}
                generateDay={generateDay}
              />
            </>
          ) : null}

          {activeSelectView === SELECT_VIEW.MONTH || isMonthPicker ? (
            <PopulateMonths
              onSelectMonth={onSelectMonth}
              displayDate={displayDate}
              setIsOpen={setIsOpen}
              isMonthPicker={isMonthPicker}
            />
          ) : null}

          {activeSelectView === SELECT_VIEW.YEAR || isYearPicker ? (
            <PopulateYears
              onSelectYear={onSelectYear}
              displayDate={displayDate}
              setIsOpen={setIsOpen}
              isYearPicker={isYearPicker}
            />
          ) : null}

          {activeSelectView === SELECT_VIEW.HOUR ? (
            <PopulateHour
              onSelectHour={onSelectHour}
              displayDate={displayDate}
            />
          ) : null}

          {activeSelectView === SELECT_VIEW.MINUTE ? (
            <PopulateMinute
              onSelectMinute={onSelectMinute}
              displayDate={displayDate}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
